<script lang="ts" module>
  import type { AccordionItem } from 'melt/builders';

  import SmallGhostButton from '@/ui/elements/buttons/SmallGhostButton.svelte';
  import ExpandableCard from '@/ui/elements/card/ExpandableCard.svelte';
  import Checkbox from '@/ui/elements/checkboxcomp/Checkbox.svelte';
  import BrandFitResult, {
    type Variant as BrandFitResultVariant,
  } from '@/ui/elements/pills/BrandFitResult.svelte';
  import Improvement from '@/ui/elements/pills/Improvement.svelte';
  import { useRVSubscription } from '@/utils/reactive-value/use-rv.svelte';
  import { useActiveText } from '@/utils/use-teo-event-observer.svelte';

  import Markdown from '../../markdown/Markdown.svelte';
  import AISuggestions from './AISuggestions/AISuggestions.svelte';
  import type { AssistantCardProps } from './assistent-types';
  import { getAccordionContext } from './helpers/accordion-context';

  export type AssistantVariant = 'wording' | 'phrases' | 'recommendation' | 'spelling' | 'grammar';

  export namespace AiAssistantCard {
    interface OwnProps {
      onImprovementClick: (improvementKey: string) => void;
      onLoadAiSuggestionClick: (type: string) => Promise<void>;
    }

    export type Props = Omit<AccordionItem<AssistantCardProps> & OwnProps, '#private'>;
  }
</script>

<script lang="ts">
  let {
    item: card,
    onImprovementClick,
    onLoadAiSuggestionClick,
    isExpanded,
  }: AiAssistantCard.Props = $props();
  let teoText = useActiveText();

  let brandFitResultVariant: BrandFitResultVariant = card.variant;

  let aiLoading = useRVSubscription(card.aiLoading);
  let isChecked = useRVSubscription(card.isChecked);

  // let lastImprovementClicked: string = $state('');

  let accordion = getAccordionContext();

  let relAiSuggestions = $derived(
    (card.aiSuggestions ?? []).map((suggestion) => ({
      ...suggestion,
      teo_result: {
        tpi: {
          percentage:
            suggestion.teo_result.tpi.percentage - (teoText.current.analysis?.data.tpi ?? 0),
        },
      },
    })),
  );
</script>

<ExpandableCard useAccordion={{ accordion, item: card }} expanded={isExpanded ?? true}>
  {#snippet header()}
    <div class="flex w-full items-center py-1">
      <p class="flex-1 text-left align-middle font-sans text-sm font-normal text-c">
        {card.cardLabel}:
      </p>
      <BrandFitResult variant={isChecked.current ? 'fixed' : brandFitResultVariant}
        >{card.variantLabel}</BrandFitResult
      >
    </div>
  {/snippet}
  {#snippet content()}
    <div class="grid gap-2 py-2">
      <div class="grid gap-3">
        {#if card.contentText}
          <hr class="border-t-c-divider" />
          <Markdown markdown={card.contentText} />

          <Checkbox
            item={{ id: 0, label: card.checkedText, checked: isChecked.current }}
            onChecked={(item) => {
              console.log('checked', item.checked);
              isChecked.current = item.checked ?? false;
              card.onCheckedClick?.(card.key, item.checked ?? false);
            }}
          ></Checkbox>
        {/if}

        {#if card.improvements.length}
          <hr class="border-t-c-divider" />
          {#if card.improvementsLabel}
            <p class="font-sans text-sm font-normal text-c-base">{card.improvementsLabel}</p>
          {/if}
          {#each card.improvements as improvement (improvement.key)}
            <Improvement
              onclick={(improvementKey) => {
                onImprovementClick?.(improvementKey);
                // lastImprovementClicked = improvementKey;
              }}
              {...improvement}
            />
          {/each}
        {/if}
        <AISuggestions
          onRemoveSuggestion={card.onRemoveAiSuggestion}
          suggestions={relAiSuggestions}
          loading={aiLoading.current}
          onSuggestionUsed={() => (isChecked.current = true)}
          {onLoadAiSuggestionClick}
          promptType={card.improvements?.length ? 'buttons' : 'dropdown'}
        ></AISuggestions>
      </div>

      {#if card.infoTextLabel && card.infoText}
        <hr class="border-t-c-divider" />
        <div class="text-right">
          <SmallGhostButton
            icon="InfoOutline"
            label={card.infoText}
            onclick={() => {
              card.onInfoClick?.();
            }}
          ></SmallGhostButton>
        </div>
      {/if}
    </div>
  {/snippet}
</ExpandableCard>
